/**
 * The browser's implementations of the ports the live editor takes
 * (`@acp/api-client/ports.js`): timers, the WebSocket, and random bytes for
 * the session's writer id and row ids. Globals are touched here and nowhere
 * below it (CLAUDE.md → explicit dependency injection); Node 24 has the same
 * globals, so the unit tests load this module too.
 */
import type { SocketFactory, SocketLike, Timers } from '@acp/api-client/ports.js';

export const browserTimers: Timers = {
  after: (ms, fn) => {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
  },
  every: (ms, fn) => {
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
  },
};

/** What `socketFactory` needs of a WebSocket class — the browser's (and Node 24's) global one is such a class. */
export type WebSocketClass = new (
  url: string,
) => {
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  send: (data: string) => void;
  close: () => void;
};

/** A WebSocket class as a `SocketLike` factory (the watch client assigns the handlers after opening). */
export function socketFactory(Ws: WebSocketClass): SocketFactory {
  return (url) => {
    const ws = new Ws(url);
    const socket: SocketLike = {
      onopen: null,
      onmessage: null,
      onclose: null,
      onerror: null,
      send: (data) => ws.send(data),
      close: () => ws.close(),
    };
    ws.onopen = () => socket.onopen?.();
    ws.onmessage = (event) => socket.onmessage?.({ data: event.data });
    ws.onclose = (event) => socket.onclose?.({ code: event.code, reason: event.reason });
    ws.onerror = (event) => socket.onerror?.(event);
    return socket;
  };
}

export const browserSocket: SocketFactory = socketFactory(WebSocket);

export function browserRandomBytes(count: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(count));
}
